/**
 * Spinner - Animated loading indicator for async operations.
 * Uses a CSS border-spin technique: a colored border with a transparent top
 * segment creates the spinning arc effect.
 *
 * Includes an accessible role="status" and a visually-hidden label so screen
 * readers announce the loading state.
 *
 * Used standalone for page/section loading, and embedded inside the Button
 * component during its isLoading state.
 *
 * @example
 * <Spinner size="md" />
 * <Spinner size="lg" label="Loading work orders" />
 */
import React from 'react';

// --- Type definitions ---

/** Available spinner size presets */
export type SpinnerSize = 'sm' | 'md' | 'lg';

/**
 * Props for the Spinner component.
 */
export interface SpinnerProps {
	/** Size preset controlling dimensions and border thickness — defaults to 'md' */
	size?: SpinnerSize;
	/** Additional CSS classes to merge onto the wrapper */
	className?: string;
	/** Accessible label announced by screen readers — defaults to 'Loading' */
	label?: string;
}

// --- Size mappings ---
// Larger sizes use thicker borders (border-4) to stay visually proportional.
const sizeClasses: Record<SpinnerSize, string> = {
	sm: 'h-16px w-16px border-2',
	md: 'h-32px w-32px border-2',
	lg: 'h-48px w-48px border-4',
};

const Spinner: React.FC<SpinnerProps> = ({ size = 'md', className = '', label = 'Loading' }) => {
	return (
		// role="status" creates a live region so screen readers announce the loading state.
		// aria-label provides the textual description of what's happening.
		<div role="status" aria-label={label} data-ref="atom-spinner" className={`spinner inline-flex ${className}`}>
			{/* The spinning circle: border-brand-primary colors most of the ring,
				border-t-transparent removes the top segment to create the "gap" effect.
				animate-spin applies the continuous CSS rotation. */}
			<div className={`spinner__circle animate-spin rounded-full border-brand-primary border-t-transparent ${sizeClasses[size]}`} />
			{/* Visually hidden text for screen readers — provides context when the spinner is shown */}
			<span className="sr-only">{label}</span>
		</div>
	);
};

export default Spinner;
